import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["subject", "body", "button", "feedback", "dialog"]

  disconnect() {
    if (this.hasDialogTarget) this.dialogTarget.close()
  }

  async copy(event) {
    const source = event.currentTarget.dataset.copySource
    if (!["subject", "body", "button"].includes(source)) return
    const target = this[`${source}Target`]
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable")
      await navigator.clipboard.writeText(target.value)
    } catch (_) {
      // Supports browsers that disallow the modern clipboard API. Keep this
      // within the explicit user click; never copy on page load.
      const previous = document.activeElement
      target.hidden = false
      target.classList.add("admin-copy-clipboard-fallback")
      target.select()
      let copied = false
      try { copied = document.execCommand("copy") } catch (_) { /* show manual fallback */ }
      target.hidden = true
      target.classList.remove("admin-copy-clipboard-fallback")
      previous?.focus()
      if (!copied) {
        this.feedback("Your browser blocked copying. Open View email and select the text to copy it manually.")
        return
      }
    }
    this.feedback(`${source === "body" ? "Message" : source === "button" ? "Button wording" : "Subject"} copied. Variables are preserved.`)
  }

  async like(event) {
    const button = event.currentTarget
    if (button.disabled) return
    button.disabled = true
    try {
      const response = await fetch(button.dataset.likeUrl, {
        method: "PUT", credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body: JSON.stringify({ liked: button.getAttribute("aria-pressed") !== "true" })
      })
      if (!response.ok || response.redirected) throw new Error("Could not save favourite")
      const result = await response.json()
      button.setAttribute("aria-pressed", String(result.liked))
      button.querySelector("[data-like-count]").textContent = result.count
      this.feedback(result.liked ? "Saved to your favourites." : "Removed from your favourites.")
    } catch (_) {
      this.feedback("Couldn’t save your favourite. Please try again; you may need to sign in again.")
    } finally {
      button.disabled = false
    }
  }

  openPreview(event) {
    // Cards have their own copy controller; the containing library owns the dialog.
    const library = this.element.closest(".admin-copy-library")
    const dialog = library?.querySelector("dialog")
    if (!dialog) return
    dialog._copyOpener = event.currentTarget
    const frame = dialog.querySelector("turbo-frame")
    if (frame.getAttribute("src") !== event.currentTarget.getAttribute("href")) {
      frame.textContent = "Loading email…"
    }
    dialog.showModal()
  }

  closePreview() { this.dialogTarget.close() }

  dialogClosed() { this.dialogTarget._copyOpener?.focus() }

  backdrop(event) { if (event.target === this.dialogTarget) this.closePreview() }

  previewFailed(event) {
    event.preventDefault()
    const frame = this.dialogTarget.querySelector("turbo-frame")
    frame.textContent = "Couldn’t load this email. Close the preview and try again, or sign in again if your session expired."
  }

  feedback(message) { if (this.hasFeedbackTarget) this.feedbackTarget.textContent = message }
}
